import React, { useState, useEffect } from "react";
import WordCloud from "react-d3-cloud";
import "./WordCloud.css";

const colorSchemes = {
    Classic: ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"],
    OceanWaves: ["#4F97A3", "#5D8AA8", "#BCD4E6", "#73C2FB", "#63B8DD", "#6495ED", "#ADD8E6"],
    SunsetGlow: ["#FF4500", "#FF6347", "#FF7F50", "#FFA07A", "#DA70D6", "#BA55D3", "#9400D3"],
    PastelDream: ["#e6a8d7", "#e58c8a", "#ffcc99", "#f4d03f", "#b2dfdb", "#64b5f6", "#bb86fc", "#e1bee7"],
    FireAndIce: ["#ff4500", "#ff8c00", "#ffd700", "#00bfff", "#1e90ff", "#4682b4", "#dc143c", "#b22222"],
    MidnightMystery: ["#8B008B", "#6F4685", "#5d3fd3", "#7d5fff", "#9a40d3", "#b05fd3", "#d590ff"],
    Monochrome: ["#54626F", "#5E716A", "#686868", "#928E85", "#A2A2A2", "#B29CA2", "#8a7f8d"],
};

const GratitudeWordCloud = ({ words = [] }) => {
  
  const [dimensions, setDimensions] = useState({
    width: Math.min(window.innerWidth * 0.95, 768),
    height: 800,
  });

  const [selectedColorScheme, setSelectedColorScheme] = useState("Classic");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const updateSize = () => {
        const baseWidth = Math.min(window.innerWidth * 0.95, 768);
        const baseHeight = 250;
        const isMobile = window.innerWidth < 768;

        const extraHeight = words.length > 10
        ? (words.length - 0) * (isMobile ? 8.3 : 3.65)
        : 0;

        const maxHeight = 3000;

        setDimensions({
          width: baseWidth,
          height: Math.min(baseHeight + extraHeight, maxHeight),
        });
      };

      updateSize();
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }
  }, [words.length]);

  if (!words || words.length === 0) {
    console.log("⚠️ No words received for Word Cloud");
    return <p className="gratitude-message">What are you grateful for?</p>;
  }

  const getWordFrequencies = (words) => {
    if (!Array.isArray(words)) return [];

    const wordCount = {};

    words
      .filter((word) => typeof word === "string" && word.trim() !== "")
      .map((word) => word.trim().toLowerCase())
      .forEach((word) => {
        wordCount[word] = (wordCount[word] || 0) + 1;
      });

    return Object.keys(wordCount).map((word) => ({
      text: word,
      value: wordCount[word] * 400,
    }));
  };

  const wordData = getWordFrequencies(words);

  const fontSizeMapper = (word) => {
    // Decrease word size for mobile (scale by 0.8)
    const scale = window.innerWidth < 768 ? 0.7 : 1;  // 0.8 for mobile, 1 for desktop
    return Math.log2(word.value + 1) * 50 * scale + 20;  // Apply scale to font size
  };

  const rotate = () => {
    const rand = Math.random();
    if (rand < 0.5) return 0;
    if (rand < 0.75) return 90;
    return 45;
  };

  const padding = words.length > 100 ? 4 : words.length > 50 ? 5 : 8;

  const getColor = (word, index) => {
    const scheme = colorSchemes[selectedColorScheme] || colorSchemes.default;
    return scheme[index % scheme.length];
  };

  return (
    <div style={{ textAlign: "center", width: "100%" }}>
      <WordCloud
        data={wordData}
        fontSizeMapper={fontSizeMapper}
        rotate={rotate}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        padding={padding}
        width={dimensions.width}
        height={dimensions.height}
        fill={(word, index) => getColor(word, index)}
      />
      
      <div className="color-dropdown">
        <label htmlFor="colorScheme">Select Color Scheme:</label>
        <select
          id="colorScheme"
          value={selectedColorScheme}
          onChange={(e) => setSelectedColorScheme(e.target.value)}
        >
          {Object.keys(colorSchemes).map((scheme) => (
            <option key={scheme} value={scheme}>
              {scheme.replace(/([A-Z])/g, " $1").trim()}
            </option>
          ))}
        </select>
      </div>

      <div className="mini-void"></div>
    </div>
  );
};

export default GratitudeWordCloud;
